import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import GeneralProductInformation from './GeneralProductInformation';
import AverageTareTable from './AverageTareTable';
import WeightCalculationTable from './WeightCalculationTable';
import EquipmentDescription from './EquipmentDescription';
import BasicCard from '../../../components/basicCard/BasicCard';
import { useForm } from 'antd/es/form/Form';
import { Form, Steps } from 'antd';
import SignatureDetails from './SignatureDetails';
import { updateTaskWithSync } from '../../../api/services/taskService';
import { trimValues } from '../../../utils/trimValues';
import { localDataService } from '../../../services/localDataService';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { prePackagedProductUnitEnum } from '../../../enums/MeasuringUnitEnum';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import { useUpdateTaskStatusToInProgress } from '../../../hooks/task/useUpdateTaskStatusToInProgress';

/* Tare data */
interface TareDataType {
  key: string;
  id: number;
  tareValue: number | null;
}

const initialTare = [
  {
    key: '1',
    id: 1,
    tareValue: 0,
  },
];

/* Weight calculation */
interface WeightCalculationType {
  key: string;
  id: number;
  grossAmount: number;
  tare: number;
  netQuantity: number;
  declaredQuantity: number;
  individualError: number;
}

const initialWeightedCalculation = [
  {
    key: '1',
    id: 1,
    grossAmount: 0,
    tare: 0,
    netQuantity: 0,
    declaredQuantity: 0,
    individualError: 0,
  },
];

const PrePackagedProductVerificationForm: React.FC = () => {
  const locationRoute = useLocation();
  const { t } = useTranslation('inspection');

  const [generalInfoFormData] = useForm();
  const [equipmentUsedFormData] = useForm();
  const [signatureFormData] = useForm();

  const [tareData, setTareData] = useState<TareDataType[]>(initialTare);
  const [weightData, setWeightData] = useState<WeightCalculationType[]>(initialWeightedCalculation);
  const [tareError, setTareError] = useState<string | null>(null);
  const [weightError, setWeightError] = useState<string | null>(null);

  const navigate = useNavigate();

  const [currentSection, setCurrentSection] = useState<string>('generalInfo');
  // const [currentStep, setCurrentStep] = useState<number>(0);

  const headerRef = useRef<HTMLDivElement | null>(null);

  const [taskData, setTaskData] = useState<any>();
  const [triggeredFromTask, setTriggeredFromTask] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(false);

  const [generalInfoData, setGeneralInfoData] = useState({});
  const [equipmentUsedData, setEquipmentUsedData] = useState({});

  const { updateTaskStatusToInProgress } = useUpdateTaskStatusToInProgress();
  const { successNotification, errorNotification } = useNotificationContext();

  const [selectedUnit, setSelectedUnit] = useState<string>(prePackagedProductUnitEnum.GRAMS);
  const [selectedTareOption, setSelectedTareOption] = useState<string>('P');

  /* Assign state data passed via route */
  useEffect(() => {
    const taskData = locationRoute.state;
    if (taskData) {
      getProductById(taskData?.productRefIds);
      setTriggeredFromTask(true);
      updateTaskStatusToInProgress(taskData).then((updated) => {
        setTaskData(updated || taskData);
      });
    } else {
      setTaskData(undefined);
      setTriggeredFromTask(false);
    }
  }, [locationRoute.state, updateTaskStatusToInProgress]);

  const getProductById = async (productRefIds: string[]) => {
    if (productRefIds?.length > 0) {
      const productData: any = await referenceDataSyncService.getProductDataById(productRefIds[0]);
      if (Array.isArray(productData)) {
        const productName = productData[0]?.data?.name;
        const productPresentation = productData[0]?.data?.presentation;
        const productBrand = productData[0]?.data?.brand;
        let displayProductName = productName;

        if (productPresentation && productPresentation.trim()) {
          displayProductName += ` (${productPresentation.trim()})`;
        }

        if (productBrand && productBrand.trim()) {
          displayProductName += ` - ${productBrand.trim()}`;
        }
        generalInfoFormData.setFieldValue('productName', displayProductName);
      }
    }
  };

  /* Navigate to overview on cancel confirmation */
  const handleCancel = () => {
    // reset forms
    generalInfoFormData.resetFields();
    equipmentUsedFormData.resetFields();
    signatureFormData.resetFields();
    setTareData(initialTare);
    setWeightData(initialWeightedCalculation);
    setSelectedUnit(prePackagedProductUnitEnum.GRAMS);
    setSelectedTareOption('P');
    setTareError(null);
    setWeightError(null);
    // Navigate to either overview tab or monitoring visits tab
    const activeTab = taskData?.triggeredFrom === 'inspections' ? '2' : '1';
    navigate('/inspector', { state: { activeTab } });
  };

  /* Display next section */
  const handleNext = () => {
    switch (currentSection) {
      case 'generalInfo':
        saveGeneralInfoChanges();
        setCurrentSection('calculations');
        break;

      case 'calculations':
        setCurrentSection('equipmentUsedDetails');
        break;

      case 'equipmentUsedDetails':
        saveEquipmentUsedChanges();
        setCurrentSection('signatureDetails');
        break;

      default:
        setCurrentSection('generalInfo');
    }
    scrollToTop();
  };

  const scrollToTop = () => {
    setTimeout(() => {
      headerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  /* Display previous section */
  const handleBack = () => {
    switch (currentSection) {
      case 'calculations':
        setCurrentSection('generalInfo');
        break;

      case 'equipmentUsedDetails':
        setCurrentSection('calculations');
        break;

      case 'signatureDetails':
        setCurrentSection('equipmentUsedDetails');
        break;

      default:
        setCurrentSection('generalInfo');
    }
    scrollToTop();
  };

  const saveGeneralInfoChanges = async () => {
    const generalInfoFormValues = generalInfoFormData.getFieldsValue();

    const generalInfoTrimmedValues = trimValues(generalInfoFormValues);
    generalInfoFormData.setFieldsValue(generalInfoTrimmedValues);
    await generalInfoFormData.validateFields();

    setGeneralInfoData({
      ...generalInfoTrimmedValues,
    });
  };

  const saveEquipmentUsedChanges = async () => {
    const equipmentUsedFormValues = equipmentUsedFormData.getFieldsValue();

    const equipmentUsedTrimmedValues = trimValues(equipmentUsedFormValues);
    equipmentUsedFormData.setFieldsValue(equipmentUsedTrimmedValues);
    await generalInfoFormData.validateFields();

    setEquipmentUsedData({
      ...equipmentUsedTrimmedValues,
    });
  };

  const handleSubmitProductVerification = async () => {
    // try {
    setIsLoading(true);

    const signatureFormValues = signatureFormData.getFieldsValue();

    const signatureTrimmedValues = trimValues(signatureFormValues);

    signatureFormData.setFieldsValue(signatureTrimmedValues);

    const generalInfoDataVals = {
      ...generalInfoData,
      date: generalInfoData?.date.valueOf(),
    };

    const inspectionBody = {
      date: Date.now(),
      locationId: taskData?.locationId,
      establishmentId: taskData?.establishmentId,
      establishmentOther: taskData?.establishmentOther,
      /* respondent: null,
          signatureInitial: null,
          finalRemarks: null, */
      reportedTime: Date.now(),
      taskRefId: taskData?.refId,
      prePackagedProductVerificationDetails: {
        ...generalInfoDataVals,
        avgTareTableData: tareData,
        weightCalculationTableData: {
          body: weightData,
          header: {
            selectedUnit,
            selectedTareOption,
          },
        },
        ...equipmentUsedData,
        ...signatureTrimmedValues,
      },
    };

    try {
      const result = await localDataService.createLegalVerificationInspection(inspectionBody);

      if (result.inspection) {
        triggeredFromTask && updateTaskStatusToInProgress(taskData);
        successNotification(t('prePackagedProductVerificationSuccess'));
      }

      // setCollectionSessionInfo(initialCollectionSessionInfo);

      generalInfoFormData.resetFields();
      equipmentUsedFormData.resetFields();
      signatureFormData.resetFields();
      // handleRespondentCancel();

      if (triggeredFromTask) {
        // navigate('/inspector');
        const activeTab = taskData?.triggeredFrom === 'inspections' ? '2' : '1';
        navigate('/inspector', { state: { activeTab } });
      }
    } catch (error) {
      console.error('Failed to add pre packaged product verification:', error);
      errorNotification(`${t('prePackagedProductVerificationFailed')}: ${error}`);
    } finally {
      setIsLoading(false);
    }
    /* } catch (validationError: any) {
        console.error('Form validation failed');
        errorNotification(`Form validation failed}`);
        setIsLoading(false);
      } */
  };

  const getCurrentStep = () => {
    switch (currentSection) {
      case 'generalInfo':
        return 0;
      case 'calculations':
        return 1;
      case 'equipmentUsedDetails':
        return 2;
      case 'signatureDetails':
        return 3;
      default:
        return 0;
    }
  };

  return (
    <div className="my-6 w-full" ref={headerRef}>
      {/* Header */}
      <BasicCard>
        <div className="mt-4 flex w-full justify-center">
          <div className="mt-4 text-center">
            <h1 className="mb-2 text-2xl font-bold">{t('prePackageProductMainHeading')}</h1>
            <p>{t('prePackageProductSubHeading')}</p>
          </div>
        </div>

        <div className="my-4 flex min-h-screen w-full gap-4">
          <div className="ml-0 mt-4 flex-1 overflow-y-auto rounded-lg md:ml-4">
            {/* <BasicCard> */}
            <div className="relative">
              <div className="px-6 pt-6 md:mb-4 md:mt-6 md:px-4">
                <Steps
                  size="small"
                  current={getCurrentStep()}
                  labelPlacement="vertical"
                  items={[
                    { title: t('generalProductInformation') },
                    { title: t('calculations') },
                    { title: t('equipmentDescription') },
                    { title: t('signatureDetails') },
                  ]}
                  className="section-steps"
                />
              </div>
              {/* <Form form={prePackagedProductFormData} layout="vertical"> */}

              {/* General Pre-Packaged Product Verification information */}
              {currentSection === 'generalInfo' && (
                // <BasicCard>
                <div className="p-2 md:p-4">
                  <GeneralProductInformation
                    t={t}
                    form={generalInfoFormData}
                    handleCancel={handleCancel}
                    handleBack={handleBack}
                    handleNext={handleNext}
                  />
                </div>

                // </BasicCard>
              )}

              {/* Average Tare Table */}
              {currentSection === 'calculations' && (
                // <BasicCard>
                <div className="p-2 md:p-4">
                  <AverageTareTable
                    t={t}
                    handleCancel={handleCancel}
                    handleBack={handleBack}
                    handleNext={handleNext}
                    tareData={tareData}
                    setTareData={setTareData}
                    tareError={tareError}
                    setTareError={setTareError}
                  />

                  {/* Weight Calculation */}
                  <WeightCalculationTable
                    t={t}
                    handleCancel={handleCancel}
                    handleBack={handleBack}
                    handleNext={handleNext}
                    weightData={weightData}
                    setWeightData={setWeightData}
                    selectedUnit={selectedUnit}
                    setSelectedUnit={setSelectedUnit}
                    selectedTareOption={selectedTareOption}
                    setSelectedTareOption={setSelectedTareOption}
                    tareError={tareError}
                    weightError={weightError}
                    setWeightError={setWeightError}
                    tareData={tareData}
                  />
                </div>

                // </BasicCard>
              )}

              {/* Equipment Description Section */}
              {currentSection === 'equipmentUsedDetails' && (
                // <BasicCard>
                <div className="p-2 md:p-4">
                  <EquipmentDescription
                    t={t}
                    form={equipmentUsedFormData}
                    handleCancel={handleCancel}
                    handleBack={handleBack}
                    handleNext={handleNext}
                  />
                </div>

                // </BasicCard>
              )}
              {currentSection === 'signatureDetails' && (
                <div className="p-2 md:p-4">
                  <SignatureDetails
                    form={signatureFormData}
                    handleCancel={handleCancel}
                    handleBack={handleBack}
                    handleSubmit={handleSubmitProductVerification}
                  />
                </div>
              )}
              {/* </Form> */}
            </div>
            {/* </BasicCard> */}
          </div>
        </div>
      </BasicCard>
    </div>
  );
};

export default PrePackagedProductVerificationForm;
